import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { getClients, getMe } from '../api/client';
import { useAuth } from '../auth/useAuth';
import { Card } from '../components/Card';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';
import { OutcomeBadge } from '../components/OutcomeBadge';

export function ClientsListPage() {
  const { getIdToken } = useAuth();

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => getMe(getIdToken) });
  const clientsQuery = useQuery({
    queryKey: ['clients'],
    queryFn: () => getClients(getIdToken),
    enabled: meQuery.isSuccess,
  });

  // Community accounts belong on the Residents dashboard, not the individual
  // Family view — redirect if one lands here directly.
  if (meQuery.data?.accountType === 'community') {
    return <Navigate to="/community" replace />;
  }
  if (meQuery.isLoading || clientsQuery.isLoading) {
    return <p className="text-hs-text-mute">Loading…</p>;
  }
  if (meQuery.error || clientsQuery.error) {
    return (
      <p role="alert" className="text-hs-danger-text">
        We couldn't load your family right now. Please refresh to try again.
      </p>
    );
  }

  const clients = clientsQuery.data?.clients ?? [];

  return (
    <>
      <PageHeader
        title="Your Family"
        subtitle={clients.length === 1 ? "1 person you’re keeping an eye on." : `${clients.length} people you’re keeping an eye on.`}
      />

      {clients.length === 0 ? (
        <EmptyState title="No family members yet">
          Your administrator will add the people you care about. They'll appear here once they're set up.
        </EmptyState>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 list-none p-0 m-0">
          {clients.map((c) => (
            <Card
              key={c.memberId}
              as="li"
              className="p-4 transition-shadow hover:shadow-hs-md hover:border-hs-primary/30"
            >
              <Link to={`/clients/${c.memberId}`} className="flex flex-col gap-2.5 no-underline">
                <div className="flex items-center justify-between">
                  <span className="font-display font-semibold text-[18px] tracking-hs-tight text-hs-text">
                    {c.displayName}
                  </span>
                  <OutcomeBadge outcome={c.lastCheckIn?.outcome ?? null} />
                </div>
                {c.lastCheckIn ? (
                  <>
                    <p className="text-[13.5px] text-hs-text-mute leading-relaxed line-clamp-2">
                      {c.lastCheckIn.summary}
                    </p>
                    {c.lastCheckIn.completedAt && (
                      <span className="text-[12px] text-hs-text-mute">
                        {new Date(c.lastCheckIn.completedAt).toLocaleString()}
                      </span>
                    )}
                  </>
                ) : (
                  <p className="text-[13.5px] text-hs-text-mute">No check-ins yet.</p>
                )}
              </Link>
            </Card>
          ))}
        </ul>
      )}
    </>
  );
}
